// Text encode/decode dev tools (Base64, JWT). Pure, browser-local.

export type EncodeVariant =
  | 'base64-encode'
  | 'base64-decode'
  | 'jwt-decoder'
  | 'url-encode'
  | 'url-decode'
  | 'html-encode'
  | 'html-decode';

export const encodeMeta: Record<
  EncodeVariant,
  { badge: string; title: string; inputLabel: string; outputLabel: string; sample: string }
> = {
  'base64-encode': {
    badge: 'Base64',
    title: 'Encode text to Base64',
    inputLabel: 'Text to encode',
    outputLabel: 'Base64',
    sample: 'Hello, world!',
  },
  'base64-decode': {
    badge: 'Base64',
    title: 'Decode Base64 to text',
    inputLabel: 'Base64 to decode',
    outputLabel: 'Decoded text',
    sample: 'SGVsbG8sIHdvcmxkIQ==',
  },
  'jwt-decoder': {
    badge: 'JWT',
    title: 'Decode a JSON Web Token',
    inputLabel: 'JWT',
    outputLabel: 'Decoded header and payload',
    sample:
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIn0.abc',
  },
  'url-encode': {
    badge: 'URL',
    title: 'Encode text for URLs',
    inputLabel: 'Text to encode',
    outputLabel: 'URL-encoded',
    sample: 'name=John Doe & city=New York',
  },
  'url-decode': {
    badge: 'URL',
    title: 'Decode a URL-encoded string',
    inputLabel: 'URL-encoded text',
    outputLabel: 'Decoded text',
    sample: 'name%3DJohn%20Doe%20%26%20city%3DNew%20York',
  },
  'html-encode': {
    badge: 'HTML',
    title: 'Encode text to HTML entities',
    inputLabel: 'Text to encode',
    outputLabel: 'HTML entities',
    sample: '<div class="box"> Tom & Jerry </div>',
  },
  'html-decode': {
    badge: 'HTML',
    title: 'Decode HTML entities to text',
    inputLabel: 'HTML entities',
    outputLabel: 'Decoded text',
    sample: '&lt;div class=&quot;box&quot;&gt; Tom &amp; Jerry &lt;/div&gt;',
  },
};

function encodeBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function decodeBase64(text: string): string {
  const cleaned = text.trim();
  if (!cleaned) return '';
  const binary = atob(cleaned);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function decodeBase64Url(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  return decodeBase64(padded + '='.repeat((4 - (padded.length % 4)) % 4));
}

function decodeJwt(token: string): string {
  const parts = token.trim().split('.');
  if (parts.length < 2) {
    throw new Error('A JWT has three parts separated by dots.');
  }
  const prettify = (segment: string, label: string) => {
    try {
      return `${label}:\n${JSON.stringify(JSON.parse(decodeBase64Url(segment)), null, 2)}`;
    } catch {
      throw new Error(`Could not decode the JWT ${label.toLowerCase()}.`);
    }
  };
  return `${prettify(parts[0], 'Header')}\n\n${prettify(parts[1], 'Payload')}`;
}

const HTML_ENTITIES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

function encodeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (char) => HTML_ENTITIES[char]);
}

function decodeHtml(text: string): string {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0*39;/g, "'")
    .replace(/&#x0*27;/gi, "'")
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&');
}

export function runEncode(variant: EncodeVariant, input: string): string {
  if (!input.trim()) return '';
  if (variant === 'base64-encode') return encodeBase64(input);
  if (variant === 'base64-decode') return decodeBase64(input);
  if (variant === 'jwt-decoder') return decodeJwt(input);
  if (variant === 'url-encode') return encodeURIComponent(input);
  if (variant === 'url-decode') return decodeURIComponent(input.trim());
  if (variant === 'html-encode') return encodeHtml(input);
  return decodeHtml(input);
}
